import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Trash2, GripVertical, Settings } from 'lucide-react';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'email' | 'phone' | 'select' | 'checkbox';
  required: boolean;
  placeholder?: string;
  options?: string[]; // For select fields
  order: number;
}

interface JobApplicationFormBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  jobPosting: any | null; // Job posting object
}

const defaultFields: FormField[] = [
  { id: 'firstName', label: 'First Name', type: 'text', required: true, order: 1 },
  { id: 'lastName', label: 'Last Name', type: 'text', required: true, order: 2 },
  { id: 'email', label: 'Email Address', type: 'email', required: true, order: 3 },
  { id: 'phone', label: 'Phone Number', type: 'phone', required: false, order: 4 },
  { id: 'workExperience', label: 'Work Experience', type: 'textarea', required: false, placeholder: 'Describe your relevant work experience...', order: 5 },
  { id: 'education', label: 'Education', type: 'textarea', required: false, placeholder: 'Describe your educational background...', order: 6 },
  { id: 'skills', label: 'Skills', type: 'textarea', required: false, placeholder: 'List your relevant skills...', order: 7 },
  { id: 'availability', label: 'Availability', type: 'textarea', required: false, placeholder: 'When can you start? What are your preferred hours?', order: 8 }
];

const JobApplicationFormBuilder: React.FC<JobApplicationFormBuilderProps> = ({
  isOpen,
  onClose,
  jobPosting
}) => {
  const [fields, setFields] = useState<FormField[]>(defaultFields);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && jobPosting) {
      loadFormConfiguration();
    }
  }, [isOpen, jobPosting]);

  const loadFormConfiguration = async () => {
    try {
      setLoading(true);
      
      // Check if the job posting has custom form configuration
      if (jobPosting?.application_form_config) {
        setFields(jobPosting.application_form_config);
      } else {
        // Use default fields if no custom configuration exists
        setFields(defaultFields);
      }
    } catch (error: unknown) {
      console.log('No existing form configuration found, using defaults');
      setFields(defaultFields);
    } finally {
      setLoading(false);
    }
  };

  const saveFormConfiguration = async () => {
    if (!jobPosting) return;
    
    try {
      setSaving(true);
      
      // Save the form configuration to the job posting
      const { error } = await supabase
        .from('job_postings')
        .update({ application_form_config: fields })
        .eq('id', jobPosting.id);

      if (error) throw error;
      
      toast.success("Application form customized successfully!", {
        description: "Changes will be applied to new job applications."
      });
      
      onClose();
    } catch (error: unknown) {
      console.error('Error saving form configuration:', error);
      toast.error("Failed to save form configuration.");
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    const newField: FormField = {
      id: `custom_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      order: fields.length + 1
    };
    setFields([...fields, newField]);
  };

  const updateField = (fieldId: string, updates: Partial<FormField>) => {
    setFields(prev => 
      prev.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
  };

  const removeField = (fieldId: string) => {
    // Don't allow removing required core fields
    const coreFields = ['firstName', 'lastName', 'email'];
    if (coreFields.includes(fieldId)) {
      toast.error("Cannot remove required core fields.");
      return;
    }
    
    setFields(prev => prev.filter(field => field.id !== fieldId));
  };

  const moveField = (fieldId: string, direction: 'up' | 'down') => {
    const fieldIndex = fields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) return;
    
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    
    if (targetIndex >= 0 && targetIndex < newFields.length) {
      [newFields[fieldIndex], newFields[targetIndex]] = [newFields[targetIndex], newFields[fieldIndex]];
      
      // Update order values
      newFields.forEach((field, index) => {
        field.order = index + 1;
      });
      
      setFields(newFields);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Customize Application Form
          </DialogTitle>
          <DialogDescription>
            Configure the fields that candidates will see when applying for: {jobPosting?.title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-6 h-[70vh]">
          {/* Form Builder */}
          <div className="w-1/2 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Form Fields</h3>
              <Button onClick={addField} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </Button>
            </div>
            
            <div className="space-y-3 overflow-y-auto max-h-[55vh]">
              {fields.map((field, index) => (
                <Card key={field.id} className="p-3">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                        <Input
                          value={field.label}
                          onChange={(e) => updateField(field.id, { label: e.target.value })}
                          className="text-sm"
                          placeholder="Field label"
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveField(field.id, 'up')}
                          disabled={index === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveField(field.id, 'down')}
                          disabled={index === fields.length - 1}
                        >
                          ↓
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeField(field.id)}
                          disabled={['firstName', 'lastName', 'email'].includes(field.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={field.type}
                        onValueChange={(value: unknown) => updateField(field.id, { type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="textarea">Textarea</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="select">Select</SelectItem>
                          <SelectItem value="checkbox">Checkbox</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={field.required}
                          onCheckedChange={(checked) => updateField(field.id, { required: checked })}
                        />
                        <Label className="text-sm">Required</Label>
                      </div>
                    </div>
                    
                    <Input
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                      placeholder="Placeholder text"
                      className="text-sm"
                    />
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="w-1/2 border-l pl-6">
            <h3 className="text-lg font-semibold mb-4">Preview</h3>
            <div className="space-y-4 overflow-y-auto max-h-[55vh] bg-muted/20 p-4 rounded-lg">
              <div className="text-sm text-muted-foreground mb-4">
                This is how the form will appear to candidates:
              </div>
              
              {fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label className="text-sm">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  
                  {field.type === 'textarea' ? (
                    <Textarea
                      placeholder={field.placeholder}
                      disabled
                      className="bg-background"
                      rows={3}
                    />
                  ) : field.type === 'select' ? (
                    <Select disabled>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </Select>
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" disabled className="rounded" />
                      <span className="text-sm">{field.placeholder || 'Checkbox option'}</span>
                    </div>
                  ) : (
                    <Input
                      type={field.type === 'phone' ? 'tel' : field.type}
                      placeholder={field.placeholder}
                      disabled
                      className="bg-background"
                    />
                  )}
                </div>
              ))}
              
              <Button disabled className="w-full mt-6">
                Submit Application
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={saveFormConfiguration} disabled={saving}>
            {saving ? 'Saving...' : 'Save Form Configuration'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationFormBuilder;